import React, { useState, useEffect, useRef } from 'react';
import { CVData } from './CVContent';

// Auto-resizing textarea component
const AutoTextArea = ({ value, onChange, className }) => {
  const textAreaRef = useRef(null);

  useEffect(() => {
    if (textAreaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textAreaRef.current.style.height = 'auto';
      // Set height to scrollHeight to fit content
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textAreaRef}
      value={value}
      onChange={onChange}
      className={`${className} overflow-hidden resize-none`}
      rows={1}
    />
  );
};

const CVVisualEditor = ({ onSave }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    // Deep clone to avoid mutating the original imported object directly
    setData(JSON.parse(JSON.stringify(CVData)));
  }, []);

  if (!data) return <div>Loading...</div>;

  const handleChange = (field, value) => {
    setData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (section, index, field, value) => {
    setData(prev => {
      const newArray = [...prev[section]];
      newArray[index] = { ...newArray[index], [field]: value };
      return { ...prev, [section]: newArray };
    });
  };

  const handleSave = () => {
    onSave(JSON.stringify(data, null, 2));
  };

  return (
    <div className="space-y-8 text-left">
      <div className="flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800 p-4 z-10 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Visual Editor: CVContent</h2>
        <button 
          onClick={handleSave}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
        >
          Apply Changes to JSON
        </button>
      </div>

      {/* Personal Info Section */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Profession</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.profession}
              onChange={(e) => handleChange('profession', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input 
              type="email"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.address}
              onChange={(e) => handleChange('address', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">LinkedIn</label>
            <input 
              type="text"
              className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              value={data.linkedIn}
              onChange={(e) => handleChange('linkedIn', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Professional Summary */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Professional Summary</h3>
        <AutoTextArea 
          className="w-full p-2 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
          value={data.professional_summary}
          onChange={(e) => handleChange('professional_summary', e.target.value)}
        />
      </div>

      {/* Professional Experience */}
      <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg border border-gray-200 dark:border-gray-600">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Professional Experience</h3>
        <div className="space-y-8">
          {data.professional_experience.map((exp, index) => (
            <div key={index} className="p-4 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Job Title</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.title}
                    onChange={(e) => handleArrayChange('professional_experience', index, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Company</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.company}
                    onChange={(e) => handleArrayChange('professional_experience', index, 'company', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Location</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.location}
                    onChange={(e) => handleArrayChange('professional_experience', index, 'location', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Date</label>
                  <input 
                    type="text" 
                    className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                    value={exp.date}
                    onChange={(e) => handleArrayChange('professional_experience', index, 'date', e.target.value)}
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Highlight</label>
                <AutoTextArea 
                  className="w-full p-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 dark:text-white" 
                  value={exp.highlight}
                  onChange={(e) => handleArrayChange('professional_experience', index, 'highlight', e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CVVisualEditor;